import { createFileRoute } from '@tanstack/react-router';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { ErrorState, LoadingShell } from '@siliconx/shared/ui/state';
import { KpiStrip } from '../../components/environmental/KpiStrip';
import { BasisTag } from '../../components/environmental/BasisTag';
import { FyGoalTrackingCard } from '../../components/environmental/FyGoalTrackingCard';
import { DataCoverageStrip } from '../../components/financials/sustainability/DataCoverageStrip';
import { ClassBreakdownBars } from '../../components/environmental/fleet-detail/ClassBreakdownBars';
import { MonthlyEmissionsMwhChart } from '../../components/environmental/fleet-detail/MonthlyEmissionsMwhChart';
import { SiteDumbbell } from '../../components/environmental/fleet-detail/SiteDumbbell';
import { SecondLifeLedger } from '../../components/environmental/fleet-detail/SecondLifeLedger';
import { TopEmittingAssets } from '../../components/environmental/fleet-detail/TopEmittingAssets';
import { useAvoidedEventsTape, useKpiAvoided, useMonthlySeries } from '../../hooks/useEnvironmentalOverview';
import {
  useClassBreakdown,
  useFleetDetailQuery,
  useMonthlyMwh,
  useSiteBreakdown,
  useTopEmitters,
} from '../../hooks/useEnvironmentalFleetDetail';

/**
 * SIL-476 — Fleet detail tab, post-demo live slice. Per
 * `Environmental Fleet Detail.dc.html` amended by the SIL-459 register
 * (resources/plans/sustainability-dashboard-spec.md §3 "Fleet detail"):
 * per-asset-class stacked bars, monthly emissions + MWh overlay, per-site
 * dumbbell, second-life ledger (Verified = transaction attestation, R6
 * tooltip), top emitting assets.
 *
 * The KPI strip and FY30 target card are the SAME components the Overview
 * tab mounts (`KpiStrip`, `FyGoalTrackingCard` — the SIL-474 b-lite
 * reduction-percent target model), sharing that tab's query cache key, so
 * this tab never disagrees with Overview about the org's totals. The
 * second-life ledger likewise reuses Overview's `useAvoidedEventsTape` data
 * (the SAME per-transaction resale events, SIL-471) rather than re-querying.
 *
 * REAL widgets (this route's own `GET /environmental/fleet-detail`):
 * per-class stacked bars, per-site dumbbell, top emitters (all 'derived' —
 * same use-phase/embodied aggregates the Overview tab's KPI strip sums, just
 * split differently), MWh overlay ('measured' — new
 * `telemetry_daily_agg` aggregation, readiness doc gap #4). Every real
 * widget here is REAL-BUT-CAVEATED where coverage is thin (class/top-emitter
 * aggregates: solid for server/gpu, 0% for four other classes per SIL-422;
 * site dumbbell: 13.8% fleet-wide `data_center_id` linkage, disclosed via
 * its own coverage figure) — never sample-gated, per the "ships caveated,
 * never gated" convention this dashboard already follows elsewhere.
 */
export const Route = createFileRoute('/environmental/fleet-detail')({
  component: FleetDetailTab,
});

function FleetDetailTab() {
  const fleetDetailQ = useFleetDetailQuery();
  const classBreakdown = useClassBreakdown();
  const monthlyMwh = useMonthlyMwh();
  const siteBreakdown = useSiteBreakdown();
  const topEmitters = useTopEmitters();

  const monthly = useMonthlySeries();
  const avoided = useKpiAvoided();
  const tape = useAvoidedEventsTape();

  return (
    <div className="space-y-4">
      <KpiStrip />

      {fleetDetailQ.isLoading && <LoadingShell rows={4} />}
      {fleetDetailQ.isError && (
        <ErrorState
          title="Could not load the Fleet detail tab"
          error={fleetDetailQ.error}
          onRetry={() => void fleetDetailQ.refetch()}
        />
      )}

      {fleetDetailQ.data && (
        <div className="grid grid-cols-12 items-start gap-4">
          <div className="col-span-12 lg:col-span-4">
            <FyGoalTrackingCard />
          </div>

          <Card padding="md" className="col-span-12 lg:col-span-8">
            <div className="mb-3 flex items-center justify-between">
              <SectionLabel>Footprint by asset class</SectionLabel>
              <BasisTag basis={classBreakdown.basis} />
            </div>
            <ClassBreakdownBars items={classBreakdown.data ?? []} />
          </Card>

          <Card padding="md" className="col-span-12">
            <div className="mb-3 flex items-center justify-between">
              <SectionLabel>Monthly emissions by scope · MWh overlay</SectionLabel>
              <div className="flex items-center gap-1.5">
                <BasisTag basis={monthly.basis} />
                <BasisTag basis="measured" />
              </div>
            </div>
            <MonthlyEmissionsMwhChart emissions={monthly.data ?? []} mwh={monthlyMwh.data?.points ?? []} />
            {monthlyMwh.data && (
              <div className="mt-3">
                <DataCoverageStrip coverage={monthlyMwh.data.coverage} />
              </div>
            )}
          </Card>

          <Card padding="md" className="col-span-12 lg:col-span-6">
            <div className="mb-3 flex items-center justify-between">
              <SectionLabel>Scope 2 vs Scope 3 by site</SectionLabel>
              <BasisTag basis={siteBreakdown.data?.basis} />
            </div>
            <SiteDumbbell items={siteBreakdown.data?.items ?? []} />
            {siteBreakdown.data && (
              <p className="mt-2 text-xs text-zinc-500">
                {siteBreakdown.data.coverage.linkedAssetCount.toLocaleString('en-US')} of{' '}
                {siteBreakdown.data.coverage.fleetAssetCount.toLocaleString('en-US')} assets (
                {siteBreakdown.data.coverage.coveragePct.toLocaleString('en-US', { maximumFractionDigits: 1 })}%)
                are linked to a site. Unlinked assets contribute nothing to the split above — that is a
                coverage gap, not a finding that they run emission-free.
              </p>
            )}
          </Card>

          <Card padding="md" className="col-span-12 lg:col-span-6">
            <div className="mb-3 flex items-center justify-between">
              <SectionLabel>Second-life ledger</SectionLabel>
              <BasisTag basis={tape.basis} />
            </div>
            <SecondLifeLedger events={tape.data} totalAvoidedKgco2e={avoided.data.valueKgco2e} />
          </Card>

          <Card padding="md" className="col-span-12">
            <div className="mb-3 flex items-center justify-between">
              <SectionLabel>Top emitting assets</SectionLabel>
              <BasisTag basis={topEmitters.basis} />
            </div>
            <TopEmittingAssets items={topEmitters.data ?? []} />
          </Card>
        </div>
      )}
    </div>
  );
}
